import {
  getManageFlowBookingById,
  setChangeBookingFlow,
  setChangeTripsSelectedTripId,
} from '@/features/manage/manageActions';
import {
  abandonExchange,
  startJourneyChangeExchange,
} from '@/features/purchase/purchaseActions';
import { selectedOutboundOfferMapSelector } from '@/features/purchase/purchaseSelectors';
import {
  findTripByFulfillment,
  resolveMatchedPassengers,
} from '@/hooks/useJourneyChangeDeepLink';
import { TransAlert } from '@/i18n/trans/alert';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type { SearchJourneyOffersDTO } from '@/types/dto/agent';
import { showErrorToast } from '@/utils/errorToast';
import {
  journeyChangePath,
  parseJourneyChangeUrlParams,
} from '@/utils/journeyChangeUrlParams';
import { getUniqueSelectedOffers } from '@/utils/purchase';
import { isNonEmptyString } from '@/utils/typeGuards';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Commits the trip picked in `JourneySelect` and hands off to the reused
// purchase `Checkout`. The token flow never populates the manage change-trips
// selection `startExchange` reads, so the commit is driven from the selected
// journey + deep-link params: load the booking into the manage slice, flag the
// change-trips flow so `Checkout` takes its `hasSelection` path, commit the
// exchange, then refetch for the new `exchangeOperations[]`.
export const useJourneyChangeCheckout = (
  selectedJourney: SearchJourneyOffersDTO | undefined
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const selectedOfferMap = useSelector(selectedOutboundOfferMapSelector);
  const [isLoading, setIsLoading] = useState(true);

  // A second invoke would create a second exchange operation for the same trip.
  const committed = useRef(false);

  // Unmounting mid-commit (e.g. browser back) leaves `commit()` running, and the
  // op it creates would later be stripped from Redux by `resetPurchase` —
  // uncancellable, blocking a second attempt. So release it here instead.
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const failCheckout = useCallback(
    (error?: ApiErrorResponse) => {
      const message = <TransAlert i18nKey="startExchangeFailed" />;
      if (error) {
        showErrorToast(message, error);
      } else {
        toast.error(message);
      }
      if (mounted.current) {
        navigate(journeyChangePath(search), { replace: true });
      }
    },
    [navigate, search]
  );

  useEffect(() => {
    if (committed.current) return;
    if (!selectedJourney) {
      setIsLoading(false);
      return;
    }
    committed.current = true;

    const releasedAfterUnmount = async () => {
      if (mounted.current) return false;
      await dispatch(abandonExchange());
      return true;
    };

    const commit = async () => {
      const {
        booking: bookingId,
        fulfillment,
        passengerExternalRef,
      } = parseJourneyChangeUrlParams();

      if (!bookingId || !fulfillment) {
        failCheckout();
        return;
      }
      // Empty means the offer-selection step was skipped
      // (a direct visit or refresh dropped the seeding).
      const offerIds = getUniqueSelectedOffers(Object.values(selectedOfferMap))
        .map(({ id }) => id)
        .filter(isNonEmptyString);
      if (offerIds.length === 0) {
        failCheckout();
        return;
      }

      try {
        const booking = await dispatch(
          getManageFlowBookingById(bookingId)
        ).unwrap();

        const tripId = findTripByFulfillment(booking, fulfillment)?.id;
        if (!tripId) {
          failCheckout();
          return;
        }

        // Same resolution the offer search used, so the committed offers match
        // the scope they were priced for.
        const passengerExternalReferences = resolveMatchedPassengers(
          booking,
          passengerExternalRef
        )
          .map((passenger) => passenger.externalReference)
          .filter(isNonEmptyString);
        if (passengerExternalReferences.length === 0) {
          failCheckout();
          return;
        }

        dispatch(setChangeBookingFlow('changeTrips'));
        dispatch(setChangeTripsSelectedTripId({ tripId, isOutbound: true }));

        await dispatch(
          startJourneyChangeExchange({
            bookingId,
            offerIds,
            passengerExternalReferences,
          })
        ).unwrap();

        if (await releasedAfterUnmount()) return;

        // Refetch so the booking carries the new PREBOOKED exchange operation.
        await dispatch(getManageFlowBookingById(bookingId)).unwrap();

        if (await releasedAfterUnmount()) return;

        setIsLoading(false);
      } catch (e) {
        await dispatch(abandonExchange());
        failCheckout(e as ApiErrorResponse);
      }
    };

    commit();
    // `selectedOfferMap` is seeded before this step mounts and read once at
    // commit time, so a new reference must not re-run the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, failCheckout, selectedJourney]);

  return { isLoading };
};
